import { array, hash } from '@ember/helper';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import eq from 'ember-truth-helpers/helpers/eq';
import gt from 'ember-truth-helpers/helpers/gt';
import or from 'ember-truth-helpers/helpers/or';
import BvBtn from 'volta/components/bv-btn';
import BvBtnGroup from 'volta/components/bv-btn-group';
import BvCardFooter from 'volta/components/bv-card/footer';
import BvCardHeader from 'volta/components/bv-card/header';
import BvCardSection from 'volta/components/bv-card/section';
import BvCardSubsection from 'volta/components/bv-card/subsection';
import BvDropdownMenu from 'volta/components/bv-dropdown-menu';
import classNames from 'volta/helpers/class-names';
import orElse from 'volta/helpers/or-else';

import type { ComponentLike, ContentValue } from '@glint/template';
import type { IBvMenuItem } from 'volta/components/bv-menu';
import type { BvBtnArgs } from 'volta/components/bv-btn';
import type { BvCardFooterSignature } from './footer';
import type { BvCardHeaderSignature, IHeaderAction } from './header';
import type { BvCardSectionSignature } from './section';
import type { BvCardSubsectionSignature } from './subsection';

type ICardArgs = BvBtnArgs & IBvMenuItem;

interface ICardAction extends ICardArgs {
  content?: ContentValue;
  icon?: string;
}

export interface BvCardSignature {
  Args: {
    embedded?: boolean;
    headerActions?: (IHeaderAction | undefined)[];
    headerAdditionalContent?: ContentValue | ComponentLike;
    onLightBg?: boolean;
    primaryFooterAction?: ICardAction;
    secondaryFooterActions?: (ICardAction | undefined)[];
    sectioned?: boolean;
    subdued?: boolean;
    subtitle?: ContentValue | ComponentLike;
    title?: ContentValue;
  };
  Blocks: {
    default: [
      {
        Section: ComponentLike<BvCardSectionSignature>;
        Subsection: ComponentLike<BvCardSubsectionSignature>;
        Header: ComponentLike<BvCardHeaderSignature>;
        Footer: ComponentLike<BvCardFooterSignature>;
      }
    ];
    headerContent: [];
  };
  Element: HTMLDivElement;
}

export default class BvCardComponent extends Component<BvCardSignature> {
  get menuItems() {
    const { secondaryFooterActions } = this.args;
    if (secondaryFooterActions?.length && secondaryFooterActions.length > 1) {
      return secondaryFooterActions;
    }
    return [];
  }

  <template>
    {{#let
      (hash
        Section=(component BvCardSection)
        Subsection=(component BvCardSubsection)
        Header=(component BvCardHeader)
        Footer=(component BvCardFooter)
      )
      as |cardComponents|
    }}
      <div
        class={{classNames
          'bv-card'
          (if @subdued 'bv-card--subdued')
          (if @onLightBg 'bv-card--on-light')
          (if @embedded 'bv-card--embedded')
        }}
        ...attributes
      >
        {{#if @title}}
          <BvCardHeader
            @actions={{orElse @headerActions (array)}}
            @title={{@title}}
            @subtitle={{@subtitle}}
          >
            {{#if (has-block 'headerContent')}}
              {{yield to='headerContent'}}
            {{else}}
              {{! @glint-expect-error: Rendering ComponentLike in a block is not supported yet }}
              {{@headerAdditionalContent}}
            {{/if}}
          </BvCardHeader>
        {{/if}}
        {{#if @sectioned}}
          <BvCardSection>
            {{yield cardComponents}}
          </BvCardSection>
        {{else}}
          {{yield cardComponents}}
        {{/if}}
        {{#if (or @primaryFooterAction @secondaryFooterActions)}}
          <BvCardFooter>
            <BvBtnGroup @segmented={{false}} as |group|>
              {{#if (eq @secondaryFooterActions.length 1)}}
                {{#each @secondaryFooterActions as |_single|}}
                  <BvBtn
                    @iconLeft={{_single.icon}}
                    @onClick={{_single.onClick}}
                    @disabled={{_single.disabled}}
                  >
                    {{_single.content}}
                  </BvBtn>
                {{/each}}
              {{else if (gt @secondaryFooterActions.length 1)}}
                <BvDropdownMenu @items={{@secondaryFooterActions}}>
                  <:trigger as |popover|>
                    <BvBtn
                      @disclosure={{true}}
                      @text={{t 'more'}}
                      @onClick={{popover.toggle}}
                      class='u-mr-2'
                      {{popover.trigger}}
                    />
                  </:trigger>
                </BvDropdownMenu>
              {{/if}}
              {{#if @primaryFooterAction}}
                <group.btn
                  @color='primary'
                  @testName={{@primaryFooterAction.testName}}
                  @text={{@primaryFooterAction.text}}
                  @iconLeft={{@primaryFooterAction.iconLeft}}
                  @iconRight={{@primaryFooterAction.iconRight}}
                  @onClick={{@primaryFooterAction.onClick}}
                  @disabled={{@primaryFooterAction.disabled}}
                  @isLoading={{@primaryFooterAction.isLoading}}
                />
              {{/if}}
            </BvBtnGroup>
          </BvCardFooter>
        {{/if}}
      </div>
    {{/let}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvCard: typeof BvCardComponent;
    'bv-card': typeof BvCardComponent;
  }
}
